import React, { useState } from "react";
import {
  Modal,
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { notify } from "./Toast";
import { changePassword } from "../services/auth";
import { useDispatch } from "react-redux";
import { logout as logoutAction } from "../redux/slices/authSlice";

const ChangePasswordDialog = ({ open, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});

  const inputFieldStyles = {
    backgroundColor: "#1e1e1e",
    borderRadius: "8px",
    border: "1px solid #333",
    color: "#fff",
  };

  const validate = () => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = "Current password is required";
    if (!newPassword) {
      newErrors.newPassword = "New password is required";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    } else if (newPassword === currentPassword) {
      newErrors.newPassword = "New password must be different from current password";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your new password";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      const response = await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      notify.success(response.message || "Password changed successfully");
      onClose();
      dispatch(logoutAction());
    } catch (err) {
      notify.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  const renderPasswordField = ({
    label,
    value,
    setValue,
    show,
    setShow,
    error,
    name,
  }) => (
    <TextField
      fullWidth
      margin="normal"
      label={label}
      type={show ? "text" : "password"}
      variant="outlined"
      value={value}
      name={name}
      onChange={(e) => {
        setValue(e.target.value);
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }}
      error={Boolean(error)}
      helperText={error}
      InputLabelProps={{ style: { color: "#aaa" } }}
      InputProps={{
        style: inputFieldStyles,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={() => setShow((prev) => !prev)}
              edge="end"
              sx={{ color: "#ccc" }}
            >
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
      }}
      className="auth-modal"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "80%", sm: 400 },
          bgcolor: "#121212",
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          color: "#fff",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Change Password
        </Typography>

        {renderPasswordField({
          label: "Current Password",
          value: currentPassword,
          setValue: setCurrentPassword,
          show: showCurrent,
          setShow: setShowCurrent,
          error: errors.currentPassword,
          name: "currentPassword",
        })}

        {renderPasswordField({
          label: "New Password",
          value: newPassword,
          setValue: setNewPassword,
          show: showNew,
          setShow: setShowNew,
          error: errors.newPassword,
          name: "newPassword",
        })}

        {renderPasswordField({
          label: "Confirm New Password",
          value: confirmPassword,
          setValue: setConfirmPassword,
          show: showConfirm,
          setShow: setShowConfirm,
          error: errors.confirmPassword,
          name: "confirmPassword",
        })}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button onClick={onClose} variant="outlined" sx={{ color: "#fff", borderColor: "#888" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#00ffff",
              color: "#000",
              '&.Mui-disabled': {
                backgroundColor: "#00ffff",
                color: "#000",
              },
            }}
            disabled={isLoading}
            onClick={handleChangePassword}
          >
            {isLoading ? <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" /> : "Change"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ChangePasswordDialog;
